import { useMutation } from '@apollo/react-hooks';
import { useRouter } from 'next/router';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { COOKIE } from '../../constants';
import { WHITE_LABEL_ACCENT_COLOR } from '../../lib/white-label';
import { useCookies } from 'react-cookie';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import ConfirmModal from '../../components/bots/ConfirmModal';
import { QuestionMarkCircleIcon } from '@heroicons/react/solid';
import PurchaseRentalDateCal from '../../components/bots/PurchaseRentalDateCal';
import {
  GET_RENTAL_MAX_DATE,
  GET_RENTAL_PRICING,
} from '../../lib/graphql/mutations';
import { toast } from 'react-toastify';
import { analytics } from '../../lib/analytics';
import { formatCentsAsUSD } from '../../lib/currency';

import Link from 'next/link';
import { getReadableDiff } from '../../utils/getReadableDiff';

export const Loader = ({ dataLoading }) => {
  return (
    dataLoading && (
      <>
        <div className="flex items-center justify-center space-x-2 animate-pulse">
          <div className="w-4 h-4 bg-blue-400 rounded-full"></div>
          <div className="w-4 h-4 bg-blue-400 rounded-full"></div>
          <div className="w-4 h-4 bg-blue-400 rounded-full"></div>
        </div>
      </>
    )
  );
};

export const RentalSideContainer = ({
  bot,
  username,
  isConfirmModalVisible,
  setIsConfirmModalVisible,
  myOwnListingIds,
  affiliateId,
  refetchBot,
  setTourOpen,
  blockScroll,
}: {
  bot: {
    id: string;
    minimumDays: number;
    name: string;
  };
  myOwnListingIds: number[];
  affiliateId?: string;
  username?: string;
  isConfirmModalVisible: boolean;
  setIsConfirmModalVisible: (v: boolean) => void;
  refetchBot: () => void;
  setTourOpen: (v: boolean) => void;
  blockScroll: () => void;
}) => {
  var localizedFormat = require('dayjs/plugin/localizedFormat');
  var timezone = require('dayjs/plugin/timezone');
  dayjs.extend(localizedFormat);
  dayjs.extend(timezone);

  const day = dayjs().local();

  let defaultStart = day
    .utc()
    .hour(21)
    .minute(0)
    .second(0)
    .millisecond(0)
    .local()
    .set('date', day.date());

  const currentDiff = dayjs().diff(defaultStart, 'hours');
  if (currentDiff > 12) {
    defaultStart = defaultStart.add(1, 'day');
  } else if (currentDiff < -12 && currentDiff > -24) {
    defaultStart = defaultStart.add(-1, 'day');
  }

  const [start, setStart] = useState(defaultStart);
  const [listingId, setListingId] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [rentalLength, setRentalLength] = useState(1);
  const [end, setEnd] = useState(start.clone().add(1, 'day'));
  const [maxEndDate, setMaxEndDate] = useState(null);
  const [selectedListingPricePerDay, setSelectedListingPricePerDay] =
    useState(0);
  const router = useRouter();

  const [cookies, setCookie] = useCookies([COOKIE.ACCESS_TOKEN]);
  const isLoggedIn = useMemo(() => {
    return !!cookies[COOKIE.ACCESS_TOKEN];
  }, [cookies]);
  // @TODO fix hydration error by moving the stuff that needs logged in into dynamic
  // const isLoggedIn = false

  const [
    getRentalPricing,
    {
      loading: rentalPriceLoading,
      error: rentalPriceError,
      data: rentalPriceData,
    },
  ] = useMutation(GET_RENTAL_PRICING);

  const [
    getRentalMaxDate,
    {
      loading: rentalMaxDateLoading,
      error: rentalMaxDateError,
      data: rentalMaxDateData,
    },
  ] = useMutation(GET_RENTAL_MAX_DATE);

  useEffect(() => {
    if (!isLoggedIn) return;
    getRentalMaxDate({
      variables: {
        startData: {
          start,
          botId: bot.id,
        },
      },
    }).then(({ data }) => {
      if (data?.getRentalListingMaxDate) {
        const maxDateData = data?.getRentalListingMaxDate;
        setMaxEndDate(dayjs(maxDateData?.maxDate));
      }
    });
  }, [start]);

  useEffect(() => {
    if (!isLoggedIn) return;
    getRentalPricing({
      variables: {
        priceData: {
          start,
          end,
          botId: bot.id,
        },
        sellerUsername: username,
      },
    })
      .then(({ data }) => {
        if (data?.getRentalPricing) {
          const priceData = data?.getRentalPricing;
          setSelectedListingPricePerDay(priceData.avgPricePerDay);
          setTotalCost(priceData.totalPrice);
          setListingId(priceData.id);
          setRentalLength(end.diff(start, 'days'));
        }
      })
      .catch((err) => {
        if (err.message === 'MUST_BE_AUTHENTICATED') {
          toast.error('Needs login');
          router.push('/login');
        }
        if (err.message === 'Username not found') {
          router.push('/bots');
        }
      });
  }, [start, end]);

  const discountedPricePerDay = useMemo(() => {
    if (rentalPriceData?.getRentalPricing?.discount?.percent) {
      return (
        selectedListingPricePerDay -
        selectedListingPricePerDay *
          (rentalPriceData.getRentalPricing.discount.percent * 0.01)
      );
    }
    return null;
  }, [selectedListingPricePerDay, rentalPriceData]);

  const quickSelect = useCallback(
    async (dur, unit) => {
      const startDay = start;
      const a = startDay.clone();
      const e = a.add(dur, unit).clone();
      setEnd(e);
    },
    [start],
  );

  const readyPurchase = useMemo(
    () =>
      listingId != 0 &&
      moment(new Date(end.toDate())).isAfter(
        moment(new Date(start.toDate())),
      ) &&
      rentalLength >= bot?.minimumDays &&
      totalCost > 0,
    [listingId, start, end, bot, rentalLength, totalCost],
  );

  const handleClickPay = useCallback(
    (e) => {
      if (!readyPurchase) return;
      if (rentalLength >= bot?.minimumDays) {
        if (myOwnListingIds && !myOwnListingIds.includes(listingId)) {
          analytics.track('Selected Rental', {
            listingId,
            rentalLength,
            botId: bot?.id,
          });
          setIsConfirmModalVisible(true);
        } else {
          toast("You can't rent your own listing!", {
            type: 'error',
          });
        }
      } else {
        return toast(`This bot requires a minimum of ${bot.minimumDays} days`, {
          type: 'error',
        });
      }
    },
    [listingId, myOwnListingIds, bot, rentalLength, readyPurchase],
  );

  const rentalCalLoading = rentalPriceLoading || rentalMaxDateLoading;

  return (
    <RentalSideCard>
      {isConfirmModalVisible && (
        <ConfirmModal
          startDate={dayjs(start)}
          endDate={dayjs(end)}
          rentalDays={rentalLength}
          bot={bot}
          listingId={listingId}
          listingPrice={selectedListingPricePerDay}
          discountPrice={discountedPricePerDay}
          totalPrice={totalCost}
          affiliateId={affiliateId}
          onClose={() => setIsConfirmModalVisible(false)}
          onError={async () => {
            await Promise.all([refetchBot()]);
          }}
          setTourOpen={setTourOpen}
          blockScroll={blockScroll}
        />
      )}
      {/* Calendar Side Container Desktop */}
      <div className="flex-1 flex-col">
        <RentalSideHeader
          bot={bot}
          blockScroll={blockScroll}
          setTourOpen={setTourOpen}
        />

        {/* Quick Select */}
        <div
          data-tour="step-3"
          className={`pb-3 mb-3 ${
            !rentalCalLoading ? 'border-b-2 border-gray-200' : ''
          }`}
        >
          {/* Quick Select Start Day */}
          <div className="flex flex-row">
            <label className="text-gray-700 block text-sm font-medium">
              Start Day
            </label>
            {/* <button
              className="ml-auto text-sm"
              style={{ color: WHITE_LABEL_ACCENT_COLOR }}
              onClick={() => {
                const diff = end.diff(start, 'days');

                const newStart = dayjs().local();

                setStart(newStart);

                setEnd(newStart.clone().add(diff, 'days'));
              }}
            >
              Start Instantly
            </button> */}
          </div>
          <PurchaseRentalDateCal
            sel={start}
            setSel={(day) => {
              setStart(day);
              setEnd(dayjs(day.toDate()).add(1, 'day'));
            }}
          />
        </div>
        <div className="mt-3 mb-3" data-tour="step-5">
          <label className="block text-sm font-medium text-gray-700">
            Quick Select
          </label>
          <div className="flex mt-1">
            <button
              onClick={() => quickSelect(1, 'day')}
              type="button"
              className="inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            >
              24 Hr
            </button>
            <button
              onClick={() => quickSelect(2, 'day')}
              type="button"
              className="-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            >
              48 Hr
            </button>
            <button
              onClick={() => quickSelect(7, 'day')}
              type="button"
              className="-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            >
              7 Days
            </button>
            <button
              onClick={() => quickSelect(30, 'day')}
              type="button"
              className="-ml-px relatwive inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            >
              30 Days
            </button>
          </div>
        </div>

        <span className="text-sm font-medium text-gray-500">
          or, select a custom end date.
        </span>

        {/* Custom Dates Section Desktop */}
        <div className="mt-4 mb-2" data-tour="step-4">
          <label className="text-gray-700 block text-sm font-medium">
            End Day
          </label>
          <Loader dataLoading={rentalCalLoading} />
          {!rentalCalLoading && (
            <PurchaseRentalDateCal
              min={start}
              sel={end}
              max={maxEndDate}
              setSel={(day) => {
                setEnd(day);
              }}
            />
          )}
        </div>
        {!isLoggedIn && (
          <div className="mt-4 mb-2 w-full text-center" data-tour="step-4">
            <p className="text-sm font-medium text-gray-500 text-center">
              <Link
                href="/login"
                className="underline"
                style={{ color: WHITE_LABEL_ACCENT_COLOR }}
              >
                Log in
              </Link>{' '}
              to check availability
            </p>
          </div>
        )}

        {rentalPriceError && (
          <div className="mt-3 border-t-2 border-gray-200" data-tour="step-6">
            <div
              className="flex items-center border border-red-500 text-red-500 text-sm font-bold px-4 py-2 rounded-md"
              role="alert"
            >
              <svg
                className="fill-current w-4 h-4 mr-2"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M12.432 0c1.34 0 2.01.912 2.01 1.957 0 1.305-1.164 2.512-2.679 2.512-1.269 0-2.009-.75-1.974-1.99C9.789 1.436 10.67 0 12.432 0zM8.309 20c-1.058 0-1.833-.652-1.093-3.524l1.214-5.092c.211-.814.246-1.141 0-1.141-.317 0-1.689.562-2.502 1.117l-.528-.88c2.572-2.186 5.531-3.467 6.801-3.467 1.057 0 1.233 1.273.705 3.23l-1.391 5.352c-.246.945-.141 1.271.106 1.271.317 0 1.357-.392 2.379-1.207l.6.814C12.098 19.02 9.365 20 8.309 20z" />
              </svg>
              <p>{rentalPriceError.message}</p>
            </div>
          </div>
        )}

        {/* Summary */}
        {!rentalPriceError && isLoggedIn && (
          <div className="mt-3 border-t-2 border-gray-200" data-tour="step-6">
            <div className="flex flex-col rounded-lg text-left pt-6">
              <span className="text-lg font-medium text-gray-500">
                Rental Summary
              </span>
              {rentalCalLoading && (
                <div className="mt-8">
                  <Loader dataLoading={rentalCalLoading} />
                </div>
              )}
              {!rentalCalLoading && (
                <>
                  <p className="text-sm font-normal text-gray-400 mb-2">
                    Dates displayed are in{' '}
                    {Intl.DateTimeFormat().resolvedOptions().timeZone} timezone
                    {/* <br />
                    <span className="text-xs font-normal text-gray-400 mb-2">
                      Timezone: {' '}
                      {Intl.DateTimeFormat().resolvedOptions().timeZone}
                    </span> */}
                  </p>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm font-normal text-gray-500">
                      Starts
                    </span>
                    <span className="text-base font-medium text-gray-600 pt-1">
                      {dayjs(start).format('L') +
                        ' at ' +
                        dayjs(start).format('LT')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm font-normal text-gray-500">
                      Ends
                    </span>
                    <span className="text-base font-medium text-gray-600 pt-1">
                      {dayjs(end).format('L') +
                        ' at ' +
                        dayjs(end).format('LT')}{' '}
                    </span>
                  </div>
                  {/* <div className="flex justify-between items-center mt-4">
                          <span className="text-sm font-normal text-gray-500">
                            Length
                          </span>
                          <span className="text-base font-medium text-gray-600 pt-1">{`${rentalLength} Days`}</span>
                        </div> */}
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm font-normal text-gray-500">
                      Cost
                    </span>
                    {rentalPriceData?.getRentalPricing?.discount && (
                      <span
                        className="text-sm font-normal text-gray-500 border rounded-md px-2 py-1"
                        style={{
                          borderColor: WHITE_LABEL_ACCENT_COLOR,
                        }}
                      >
                        {rentalPriceData?.getRentalPricing?.discount?.percent}%
                        off
                      </span>
                    )}
                    {discountedPricePerDay !== null ? (
                      <span className="text-base font-medium text-gray-600 pt-1">
                        {`${rentalLength} day${
                          rentalLength > 1 ? 's' : ''
                        } for ${formatCentsAsUSD(totalCost)} @ `}
                        <span className="line-through text-gray-400 ">{`${formatCentsAsUSD(
                          selectedListingPricePerDay,
                        )}`}</span>
                        {` ${formatCentsAsUSD(discountedPricePerDay)}`}
                        /day
                      </span>
                    ) : (
                      <span className="text-base font-medium text-gray-600 pt-1">{`${rentalLength} day${
                        rentalLength > 1 ? 's' : ''
                      } for ${formatCentsAsUSD(totalCost)} @ ${formatCentsAsUSD(
                        selectedListingPricePerDay,
                      )}/day`}</span>
                    )}
                  </div>
                  <div className="w-full text-center mt-4">
                    {dayjs(start).isAfter(dayjs()) && (
                      <span className="text-sm font-normal text-gray-500">
                        Your rental will begin in{' '}
                        {getReadableDiff(dayjs(start))}
                      </span>
                    )}
                  </div>
                  <a
                    data-tour="step-7"
                    onClick={handleClickPay}
                    className={`mt-2 whitespace-nowrap inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-base font-medium text-white ${
                      !readyPurchase
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer hover:shadow-sm'
                    }`}
                    style={{
                      backgroundColor: `${WHITE_LABEL_ACCENT_COLOR}`,
                    }}
                  >
                    Pay
                  </a>
                  {rentalPriceData?.getRentalPricing?.potentialDiscount && (
                    <div className="w-full text-center mt-2">
                      <span className="text-sm font-normal text-gray-500">
                        <button
                          className="underline"
                          onClick={() => {
                            setEnd(
                              end
                                .clone()
                                .add(
                                  rentalPriceData?.getRentalPricing
                                    ?.potentialDiscount.dayCount - rentalLength,
                                  'day',
                                ),
                            );
                          }}
                        >
                          {' '}
                          Add{' '}
                          {rentalPriceData?.getRentalPricing?.potentialDiscount
                            .dayCount - rentalLength}{' '}
                          day
                          {rentalPriceData?.getRentalPricing?.potentialDiscount
                            .dayCount -
                            rentalLength >
                          1
                            ? 's'
                            : ''}
                        </button>{' '}
                        for{' '}
                        {
                          rentalPriceData?.getRentalPricing?.potentialDiscount
                            .percent
                        }
                        % off.
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </RentalSideCard>
  );
};

export const RentalSideCard = ({ children }) => {
  return (
    <div className="flex md:flex-1 mt-4 flex-col px-2 md:px-24 md:pt-12 md:m-4 mb-6 md:mb-0 max-w-xl rounded-xl md:bg-gray-100">
      {children}
    </div>
  );
};

export const RentalSideHeader = ({ bot, setTourOpen, blockScroll }) => {
  return (
    <div className="flex flex-row w-full">
      <p
        className="text-xl font-bold text-gray-600 mb-6 flex-col"
        data-tour="step-1"
      >
        Rent {bot.name}
        <br />
        <span className="text-sm font-medium text-gray-500">
          Requires minimum of {bot.minimumDays} days
        </span>
      </p>
      <QuestionMarkCircleIcon
        onClick={() => {
          setTourOpen(true);
          blockScroll();
        }}
        className="mr-1 h-8 w-8 text-gray-400 bg-gray-100 p-2 rounded-lg flex-col float-right"
      />
    </div>
  );
};

export default RentalSideContainer;
